import { useMutation } from '@tanstack/react-query';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import type { Document } from '../../../js/api';
import { DocumentsService } from '../../../js/api';
import { handleApiError } from '../../../js/app/utils/errorhandling';
import { BulkActionBar, BulkActionButton, RowSelectCheckbox } from '../components/bulk-action-bar';
import { ConfirmationDialog } from '../components/confirmation-dialog';
import { TooltippedText } from '../components/tooltipped-text';
import { documentsZipName, downloadDocumentsZip } from '../utils/downloadDocumentsZip';
import { useOpenDocument } from './useOpenDocument';
import { useRowSelection } from './useRowSelection';

const plural = (count: number) => (count === 1 ? 'document' : 'documents');

type BulkDeleteOption = {
  /** The documents the viewer may remove (`getDocumentCapabilities(...).canDelete`). */
  deletable: Document[];
  /** Refetch whatever the removal stales (documents list, counts, the parent's queries). */
  onDeleted: () => void;
};

/**
 * Selection of documents (staff clinician drawer tabs, the shared Documents tab in
 * `selectable` mode), and what it is for: a floating bar with Select all / Invert /
 * Deselect, "Download" (one document saves as itself, several as one zip — zipping is
 * just how several files travel), and — when the host opts in with `bulkDelete` — "Remove".
 *
 * `selectableIds` are the DOCUMENT ids that may carry a checkbox, in display order (the
 * zip lists them that way): the ones the viewer may download, plus — with `bulkDelete` —
 * the ones they may remove. `downloadableIds` narrows the zip to what the viewer may
 * download; it defaults to every selectable id. A card renders
 * `renderCheckbox(documentId, label)` in its upper-left corner; a section title bar renders
 * `renderGroupCheckbox(ids, label)` to select all of its cards. Render `bar` once, after
 * the sections.
 */
export function useDocumentSelection({
  resetKey,
  selectableIds,
  downloadableIds = selectableIds,
  zipSubject,
  testIdPrefix,
  bulkDelete,
}: {
  /** The selection clears when this changes (e.g. another clinician). */
  resetKey: string;
  selectableIds: string[];
  /** The selectable ids the viewer may download (default: all of them). */
  downloadableIds?: string[];
  /**
   * Who and what these documents are, for the zip's name: "John Joe MD Education
   * Documents" → "John Joe MD Education Documents 20261001-1532".
   */
  zipSubject: string;
  testIdPrefix: string;
  /**
   * Opt-in "Remove" bulk action (`POST /api/documents/bulk-delete/`). The server applies
   * the per-document delete rule again and skips what the viewer may not remove.
   */
  bulkDelete?: BulkDeleteOption;
}) {
  const selection = useRowSelection(resetKey);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const selectedIds = selectableIds.filter((id) => selection.selected.has(id));
  const selectedDownloadableIds = downloadableIds.filter((id) => selection.selected.has(id));
  const selectedDeletable = (bulkDelete?.deletable ?? []).filter((document) =>
    selection.selected.has(document.id)
  );

  const { openDocument, isPending: isDownloading } = useOpenDocument('Failed to download document');
  const zipMutation = useMutation({
    mutationFn: (ids: string[]) =>
      downloadDocumentsZip({ ids, filename: documentsZipName(zipSubject) }),
    onSuccess: ({ included, skipped }) => {
      const saved = `Saved ${included} ${plural(included)} as a zip`;
      toast.success(skipped > 0 ? `${saved} (${skipped} could not be included)` : saved);
      selection.clear();
    },
    onError: (error: Error) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        toast.error('None of the selected documents can be downloaded.');
        return;
      }
      if (status === 413) {
        // the server caps one zip's total size (MAX_ZIP_BYTES)
        toast.error('The selected documents are too large for one zip — select fewer.');
        return;
      }
      handleApiError(error, { messagePrefix: 'Error saving the zip' });
    },
  });
  const deleteMutation = useMutation({
    mutationFn: (ids: string[]) =>
      DocumentsService.documentsBulkDeleteCreate({ requestBody: { ids } }),
    onSuccess: ({ processed, skipped }) => {
      const removed = `Removed ${processed} ${plural(processed)}`;
      toast.success(skipped > 0 ? `${removed} (${skipped} could not be removed)` : removed);
      setIsConfirmingDelete(false);
      selection.clear();
      bulkDelete?.onDeleted();
    },
    onError: (error: Error) => {
      setIsConfirmingDelete(false);
      handleApiError(error, { messagePrefix: 'Failed to remove documents', handle403: true });
    },
  });
  const busy = zipMutation.isPending || deleteMutation.isPending || isDownloading;

  /** One document saves as itself; several save as one zip. */
  const download = (ids: string[]) => {
    if (ids.length === 1) {
      openDocument(ids[0], 'attachment');
      return;
    }
    zipMutation.mutate(ids);
  };

  const renderCheckbox = (documentId: string, label: string): React.ReactNode => (
    <RowSelectCheckbox
      checked={selection.selected.has(documentId)}
      onChange={() => selection.toggle(documentId)}
      label={`Select ${label}`}
      data-testid={`${testIdPrefix}-select-${documentId}`}
    />
  );

  const renderGroupCheckbox = (ids: string[], label: string, testId: string): React.ReactNode => {
    if (ids.length === 0) return null;
    const allSelected = selection.areAllSelected(ids);
    return (
      <RowSelectCheckbox
        checked={allSelected}
        indeterminate={!allSelected && ids.some((id) => selection.selected.has(id))}
        onChange={() => selection.setMany(ids, !allSelected)}
        label={`Select all ${label}`}
        data-testid={testId}
      />
    );
  };

  const bar = (
    <>
      <BulkActionBar
        count={selectedIds.length}
        busy={busy}
        onClear={selection.clear}
        compact
        data-testid={`${testIdPrefix}-selection-bar`}
      >
        <SelectionButtons
          testIdPrefix={testIdPrefix}
          busy={busy}
          selectedCount={selectedIds.length}
          selectableCount={selectableIds.length}
          onSelectAll={() => selection.setMany(selectableIds, true)}
          onInvert={() => {
            const inverted = selectableIds.filter((id) => !selection.selected.has(id));
            selection.clear();
            selection.setMany(inverted, true);
          }}
          onDeselect={selection.clear}
        />
        <DownloadButton
          testIdPrefix={testIdPrefix}
          busy={busy}
          count={selectedDownloadableIds.length}
          working={isDownloading || zipMutation.isPending}
          onClick={() => download(selectedDownloadableIds)}
        />
        {bulkDelete && selectedDeletable.length > 0 && (
          // a trash can says it; the tooltip and the confirm dialog spell it out
          <TooltippedText
            tooltipContent="Remove"
            tooltipClassName="tw:bg-stoplight-danger"
            spanClassName="tw:inline-flex"
          >
            <BulkActionButton
              onClick={() => setIsConfirmingDelete(true)}
              disabled={busy}
              className="tw:!bg-stoplight-danger tw:hover:!bg-stoplight-danger/90"
              aria-label="Remove selected documents"
              data-testid={`${testIdPrefix}-remove`}
            >
              <i className="fa-regular fa-fw fa-trash" aria-hidden="true" />
            </BulkActionButton>
          </TooltippedText>
        )}
      </BulkActionBar>
      {bulkDelete && (
        <BulkRemoveDialog
          open={isConfirmingDelete}
          onClose={() => {
            if (!deleteMutation.isPending) setIsConfirmingDelete(false);
          }}
          onConfirm={() => deleteMutation.mutate(selectedDeletable.map((document) => document.id))}
          isLoading={deleteMutation.isPending}
          removable={selectedDeletable.length}
          notRemovable={selectedIds.length - selectedDeletable.length}
          withSignature={selectedDeletable.filter((document) => !!document.signature).length}
        />
      )}
    </>
  );

  /** Save every downloadable document without selecting ("Download all"). */
  const downloadAll = () => download(downloadableIds);

  return {
    selectedIds,
    renderCheckbox,
    renderGroupCheckbox,
    bar,
    downloadAll,
    isDownloading: busy,
  };
}

/** Select all / Invert / Deselect. */
function SelectionButtons({
  testIdPrefix,
  busy,
  selectedCount,
  selectableCount,
  onSelectAll,
  onInvert,
  onDeselect,
}: {
  testIdPrefix: string;
  busy: boolean;
  selectedCount: number;
  selectableCount: number;
  onSelectAll: () => void;
  onInvert: () => void;
  onDeselect: () => void;
}) {
  return (
    <>
      {selectedCount < selectableCount && (
        <BulkActionButton
          onClick={onSelectAll}
          disabled={busy}
          data-testid={`${testIdPrefix}-select-all`}
        >
          Select all {selectableCount}
        </BulkActionButton>
      )}
      <BulkActionButton onClick={onInvert} disabled={busy} data-testid={`${testIdPrefix}-invert`}>
        Invert
      </BulkActionButton>
      <BulkActionButton
        onClick={onDeselect}
        disabled={busy}
        data-testid={`${testIdPrefix}-deselect`}
      >
        Deselect
      </BulkActionButton>
    </>
  );
}

/** "Download" whatever is selected (one file as itself, several as a zip); nothing for none. */
function DownloadButton({
  testIdPrefix,
  busy,
  count,
  working,
  onClick,
}: {
  testIdPrefix: string;
  busy: boolean;
  count: number;
  working: boolean;
  onClick: () => void;
}) {
  if (count === 0) return null;
  return (
    <BulkActionButton
      onClick={onClick}
      disabled={busy}
      className="tw:!bg-white tw:!text-brand-green-0 tw:hover:!bg-white/90"
      data-testid={`${testIdPrefix}-download`}
    >
      <i className="fas fa-download tw:mr-1" aria-hidden="true" />
      {working ? 'Downloading…' : 'Download'}
    </BulkActionButton>
  );
}

/** "Remove N documents?" — mirrors the single-document dialog, counts instead of a name. */
function BulkRemoveDialog({
  open,
  onClose,
  onConfirm,
  isLoading,
  removable,
  notRemovable,
  withSignature,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  removable: number;
  notRemovable: number;
  withSignature: number;
}) {
  return (
    <ConfirmationDialog
      open={open}
      onClose={onClose}
      onConfirm={onConfirm}
      title={`Remove ${removable} ${plural(removable)}?`}
      body={
        <>
          <p className="tw:text-zinc-700">
            Are you sure you want to remove the selected {plural(removable)}?
            {notRemovable > 0 && (
              <>
                {' '}
                {notRemovable} of the selected {notRemovable === 1 ? 'is' : 'are'} not yours to
                remove and will stay.
              </>
            )}
          </p>
          {withSignature > 0 && (
            <div className="tw:mt-3 tw:rounded-md tw:bg-stoplight-warning/10 tw:p-4">
              <div className="tw:flex">
                <div className="tw:flex-shrink-0">
                  <i className="fa-solid fa-triangle-exclamation tw:text-stoplight-warning" />
                </div>
                <div className="tw:ml-3">
                  <p className="tw:text-sm tw:text-stoplight-warning">
                    Removing a signed or signing document removes its signature history with it. If
                    you want to keep the signature history, replace the document instead.
                  </p>
                </div>
              </div>
            </div>
          )}
        </>
      }
      confirmText="Remove"
      confirmVariant="danger"
      isLoading={isLoading}
    />
  );
}
